import * as React from "react";
import type { Schemas } from "@contract";

export type AdminSessionInfo = Schemas["AdminSessionInfo"];
export type AdminApplication = Schemas["AdminApplication"];
type AdminApplicationList = Schemas["AdminApplicationList"];
export type QueueStatus = "pending" | "approved" | "rejected";
export type AdminRunpodAccount = Schemas["AdminRunpodAccount"];
type AdminRunpodAccountList = Schemas["AdminRunpodAccountList"];
type RunpodAccountInput = Schemas["RunpodAccountInput"];
export type AdminStorageZone = Schemas["AdminStorageZone"];
type AdminStorageZoneList = Schemas["AdminStorageZoneList"];
export type AdminTenant = Schemas["AdminTenant"];
export type AdminTenantDetail = Schemas["AdminTenantDetail"];
export type TenantSort = "name" | "balance" | "spend" | "margin";
type AdminPriceListVersion = Schemas["AdminPriceListVersion"];
export type AdminPriceList = Schemas["AdminPriceList"];
type PriceListInput = Schemas["PriceListInput"];
export type AdminEffect = Schemas["AdminEffect"];
export type AdminLocation = Schemas["AdminLocation"];
export type AdminInputCheck = Schemas["AdminInputCheck"];
type AdminInputCheckList = Schemas["AdminInputCheckList"];
export type AdminReconciliationItem = Schemas["AdminReconciliationItem"];
export type AdminWebhookHealth = Schemas["AdminWebhookHealth"];
export type AdminAuditEntry = Schemas["AdminAuditEntry"];
type AdminAuditList = Schemas["AdminAuditList"];
export type AuditFilter = { actor?: string; action?: string; tenant_id?: string; cursor?: string };

/**
 * Everything the admin panel calls on /admin-api. Its own world: the admin
 * cookie (`mha_session`) and the admin CSRF token, never the dashboard's
 * session or an API key.
 */
export interface AdminApi {
  /** null when signed out (401). */
  getSession(): Promise<AdminSessionInfo | null>;
  login(input: { login: string; password: string }): Promise<AdminSessionInfo>;
  listApplications(status: QueueStatus): Promise<AdminApplicationList>;
  approve(id: string, note: string | null): Promise<AdminApplication>;
  reject(id: string, reason: string): Promise<AdminApplication>;
  listRunpodAccounts(): Promise<AdminRunpodAccountList>;
  /** Owner admins only. The key is write-only: answers carry its fingerprint. */
  registerRunpodAccount(tenantId: string, input: RunpodAccountInput): Promise<AdminRunpodAccount>;
  /** Every tenant's storage zone and its activation schedule. Never a secret. */
  listStorageZones(): Promise<AdminStorageZoneList>;
  /** Schedules the zone's activation now; the jobs process runs it. */
  retryStorageZone(tenantId: string): Promise<AdminStorageZone>;
  listTenants(status: string | null, sort: TenantSort): Promise<{ data: AdminTenant[] }>;
  getTenant(id: string): Promise<AdminTenantDetail>;
  suspendTenant(id: string, reason: string): Promise<AdminTenantDetail>;
  reinstateTenant(id: string, reason: string): Promise<AdminTenantDetail>;
  setTenantLimits(id: string, input: { requests_per_minute: number; queue_cap: number; screens_per_minute: number; reason: string }): Promise<AdminTenantDetail>;
  /** Owner admins only. Basis points on the price list: 10000 = list price, 0 = free. */
  setTenantPriceMultiplier(id: string, input: { price_multiplier_bp: number; reason: string }): Promise<AdminTenantDetail>;
  listPriceLists(): Promise<{ data: AdminPriceListVersion[] }>;
  getPriceList(version: number): Promise<AdminPriceList>;
  /** Owner admins only. Always a new version; an existing one is never edited. */
  createPriceList(input: PriceListInput): Promise<AdminPriceList>;
  listEffects(): Promise<{ data: AdminEffect[] }>;
  toggleEffect(slug: string, enabled: boolean): Promise<AdminEffect>;
  publishEffects(slugs: string[], publishedAt: string): Promise<{ data: AdminEffect[] }>;
  listLocations(slug: string): Promise<{ data: AdminLocation[] }>;
  /** Multipart: the preview MP4 and its poster. Replaces the previous upload. */
  uploadLocationPreview(slug: string, key: string, video: File, poster: File): Promise<AdminLocation>;
  listInputChecks(cursor: string | null): Promise<AdminInputCheckList>;
  reconciliation(): Promise<{ data: AdminReconciliationItem[] }>;
  webhookHealth(): Promise<AdminWebhookHealth>;
  listAudit(filter: AuditFilter): Promise<AdminAuditList>;
}

function query(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  return q.toString();
}

export class AdminProblem extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail: string,
    readonly requestId: string | null,
  ) {
    super(detail);
    this.name = "AdminProblem";
  }
}

export function createAdminHttpApi({ base = "/admin-api", fetchImpl }: { base?: string; fetchImpl?: typeof fetch } = {}): AdminApi {
  let csrf: string | null = null;
  const doFetch: typeof fetch = fetchImpl ?? ((input, init) => fetch(input, init));

  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const form = body instanceof FormData;
    if (body !== undefined && !form) headers["Content-Type"] = "application/json";
    if (method !== "GET" && csrf) headers["X-CSRF-Token"] = csrf;
    const payload = body === undefined ? undefined : form ? body : JSON.stringify(body);
    const res = await doFetch(base + path, { method, headers, credentials: "same-origin", body: payload });
    if (!res.ok) {
      const p = (await res.json().catch(() => ({}))) as { code?: string; detail?: string; title?: string; request_id?: string };
      throw new AdminProblem(res.status, p.code ?? "internal_error", p.detail ?? p.title ?? `HTTP ${res.status}`, p.request_id ?? null);
    }
    return (await res.json()) as T;
  }
  const remember = (s: AdminSessionInfo) => {
    csrf = s.csrf_token;
    return s;
  };

  return {
    getSession: async () => {
      try {
        return remember(await call<AdminSessionInfo>("GET", "/auth/session"));
      } catch (e) {
        if (e instanceof AdminProblem && e.status === 401) return null;
        throw e;
      }
    },
    login: async (input) => remember(await call<AdminSessionInfo>("POST", "/auth/login", input)),
    listApplications: (status) => call<AdminApplicationList>("GET", `/applications?status=${status}`),
    approve: (id, note) => call<AdminApplication>("POST", `/applications/${encodeURIComponent(id)}/approve`, { note }),
    reject: (id, reason) => call<AdminApplication>("POST", `/applications/${encodeURIComponent(id)}/reject`, { reason }),
    listRunpodAccounts: () => call<AdminRunpodAccountList>("GET", "/runpod/accounts"),
    registerRunpodAccount: (tenantId, input) => call<AdminRunpodAccount>("PUT", `/tenants/${encodeURIComponent(tenantId)}/runpod`, input),
    listStorageZones: () => call<AdminStorageZoneList>("GET", "/storage/zones"),
    retryStorageZone: (tenantId) => call<AdminStorageZone>("POST", `/tenants/${encodeURIComponent(tenantId)}/storage/retry`),
    listTenants: (status, sort) => call("GET", `/tenants?${query({ status: status ?? undefined, sort })}`),
    getTenant: (id) => call("GET", `/tenants/${encodeURIComponent(id)}`),
    suspendTenant: (id, reason) => call("POST", `/tenants/${encodeURIComponent(id)}/suspend`, { reason }),
    reinstateTenant: (id, reason) => call("POST", `/tenants/${encodeURIComponent(id)}/reinstate`, { reason }),
    setTenantLimits: (id, input) => call("PATCH", `/tenants/${encodeURIComponent(id)}/limits`, input),
    setTenantPriceMultiplier: (id, input) => call("PATCH", `/tenants/${encodeURIComponent(id)}/price-multiplier`, input),
    listPriceLists: () => call("GET", "/price-lists"),
    getPriceList: (version) => call("GET", `/price-lists/${version}`),
    createPriceList: (input) => call("POST", "/price-lists", input),
    listEffects: () => call("GET", "/effects"),
    toggleEffect: (slug, enabled) => call("PATCH", `/effects/${encodeURIComponent(slug)}`, { b2b_enabled: enabled }),
    publishEffects: (slugs, publishedAt) => call("POST", "/effects/publish", { slugs, published_at: publishedAt }),
    listLocations: (slug) => call("GET", `/effects/${encodeURIComponent(slug)}/locations`),
    uploadLocationPreview: (slug, key, video, poster) => {
      const body = new FormData();
      body.set("video", video);
      body.set("poster", poster);
      return call("PUT", `/effects/${encodeURIComponent(slug)}/locations/${encodeURIComponent(key)}/preview`, body);
    },
    listInputChecks: (cursor) => call("GET", `/input-checks?${query({ cursor: cursor ?? undefined })}`),
    reconciliation: () => call("GET", "/topups/reconciliation"),
    webhookHealth: () => call("GET", "/webhooks/health"),
    listAudit: (filter) => call("GET", `/audit-log?${query(filter)}`),
  };
}

export const AdminApiCtx = React.createContext<AdminApi | null>(null);

export function useAdminApi(): AdminApi {
  const api = React.useContext(AdminApiCtx);
  if (!api) throw new Error("useAdminApi outside AdminApiCtx");
  return api;
}
